import { filterClasses } from "@swivl-mono/helpers";
import { ReactNode, useId, useRef, useState } from "react";

import { DropdownFooter } from "../DropdownContainer/Dropdown.Footer";
import { Label } from "../FormField";
import { getFormFieldWidthClass } from "../FormField/FormField.Helpers";
import { FormFieldWidth } from "../FormField/FormField.Types";
import useKeyPressAction from "../Hooks/useKeyPressAction";
import { Popover, PopoverPosition } from "../Popover/Popover";
import { SelectDropdownSearch } from "./Select.DropdownSearch";
import { getFilteredItems } from "./Select.Filtering";
import { flattenOptions, getItems } from "./Select.Items";
import { OptionsList } from "./Select.OptionsList";
import { SelectTrigger } from "./Select.Trigger";
import { useChipOverflow } from "./useChipOverflow";
import { useSelectState } from "./useSelectState";

export enum SelectOutlet {
  TRIGGER = "trigger",
  POPOVER = "popover"
}

export enum SelectVariant {
  DEFAULT = "default",
  CHIP = "chip"
}

export enum LabelPosition {
  TOP = "top",
  LEFT = "left"
}

export enum SelectSize {
  EXTRA_SMALL = "extra-small",
  SMALL = "small",
  MEDIUM = "medium"
}

export interface SelectOption {
  /** Renders the row greyed out and unselectable, but still visible and searchable. */
  disabled?: boolean;
  icon?: ReactNode;
  label: string;
  /** Optional second line under the label (e.g. a location's "Austin, TX"). Display-only — chips and the trigger still render `label` alone. */
  sublabel?: string;
  value: string;
}

export interface SelectOptionGroup {
  type: "group";
  label?: string;
  options: SelectOption[];
}

export interface SelectDivider {
  type: "divider";
  label?: string;
}

export type SelectItem = SelectOption | SelectOptionGroup | SelectDivider;

export const isOptionGroup = (item: SelectItem): item is SelectOptionGroup => {
  return "type" in item && item.type === "group";
};

export const isDivider = (item: SelectItem): item is SelectDivider => {
  return "type" in item && item.type === "divider";
};

export const isSelectOption = (item: SelectItem): item is SelectOption => {
  return "value" in item && !("type" in item);
};

export interface SelectOptionProps {
  children: ReactNode;
  value: string;
}

export interface SelectGroupProps {
  children: ReactNode;
  label?: string;
}

export interface SelectDividerProps {
  label?: string;
}

export interface SelectProps {
  children?: ReactNode;
  disabled?: boolean;
  error?: string | Error | null;
  hideClear?: boolean;
  id?: string;
  loading?: boolean;
  label?: ReactNode;
  labelPosition?: LabelPosition;
  isMultiSelect?: boolean;
  name?: string;
  onChange: (selected: string[]) => void;
  options?: SelectItem[]; // alternative to children
  placeholder?: string;
  position?: PopoverPosition;
  required?: boolean;
  isSearchable?: boolean;
  hasSearchInDropdown?: boolean;
  /**
   * When false, focusing the trigger does NOT open the menu. A click opens it
   * (via the popover toggle) and the searchable trigger only acts as a text
   * input once open. Avoids the "focus opens, click toggles closed" double-click
   * on searchable triggers. Default true (preserves existing behavior).
   */
  openOnFocus?: boolean;
  selectedValues?: string[];
  size?: SelectSize;
  variant?: SelectVariant;
  width?: FormFieldWidth;
  customTrigger?: (props: { isOpen: boolean }) => ReactNode;
  /**
   * Optional explanation shown beneath the "No results" message when the list
   * comes up empty — for lists that are narrowed by state elsewhere in the
   * form, so the reason lands when the user fails to find what they expected.
   */
  emptyStateNote?: ReactNode;
  styles?: { [Key in SelectOutlet]?: React.CSSProperties };
  onAccept?: () => void;
  /** Fires whenever the dropdown opens or closes, for any reason. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Show the selected option's `sublabel` in the TRIGGER, after its label, for
   * lists whose detail line is part of the answer rather than a hint for
   * choosing — a storage unit's size and rate, say. Single-select only, and it
   * shares the trigger's one line, so it truncates with the label.
   * Default false, so existing Selects are untouched.
   */
  showSublabelInTrigger?: boolean;
}

const Select = ({
  children,
  customTrigger,
  disabled = false,
  emptyStateNote,
  styles = {},
  onAccept,
  onOpenChange,
  error = null,
  hideClear = false,
  id,
  loading = false,
  label,
  labelPosition = LabelPosition.TOP,
  isMultiSelect = false,
  name,
  onChange,
  options: optionsProp,
  placeholder,
  position = PopoverPosition.LEFT,
  required = false,
  isSearchable = false,
  hasSearchInDropdown = false,
  openOnFocus = true,
  selectedValues,
  showSublabelInTrigger = false,
  size = SelectSize.MEDIUM,
  variant = SelectVariant.DEFAULT,
  width = FormFieldWidth.FULL
}: SelectProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleChipCount, setVisibleChipCount] = useState<number | null>(null);

  const setPopoverOpen = (open: boolean): void => {
    setIsPopoverOpen(open);
    onOpenChange?.(open);
  };

  const generatedId = useId();
  const selectId = id || generatedId;

  const isChipVariant = variant === SelectVariant.CHIP;
  const hasSearchTrigger = (isSearchable || isMultiSelect) && !hasSearchInDropdown;

  const { toggleOption, clearAll } = useSelectState({
    selectedValues,
    onChange,
    isMultiSelect
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const chipContainerRef = useRef<HTMLDivElement>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);

  // parse children or use options prop
  const items: SelectItem[] = getItems({
    children,
    components: {
      SelectDividerComponent: SelectDivider,
      SelectGroupComponent: SelectGroup,
      SelectOptionComponent: SelectOption
    },
    options: optionsProp
  });

  // flatten to get all options
  const allOptions = flattenOptions(items);
  const selectedOptions = allOptions.filter((opt) => selectedValues?.includes(opt.value));

  // chip overflow logic
  useChipOverflow({
    isMultiSelect,
    chipContainerRef,
    selectContainerRef,
    selectedOptions,
    selectedValues,
    setVisibleChipCount
  });

  useKeyPressAction({
    key: "Backspace",
    callback: () => {
      if (isMultiSelect && !isChipVariant && isInputFocused && searchQuery.length === 0 && selectedValues && selectedValues.length > 0) {
        // remove last selected option
        const updatedOptions = selectedValues.slice(0, -1);
        onChange(updatedOptions);
      }
    },
    targetEl: inputRef.current
  });

  const filteredItems = getFilteredItems({
    hasSearchInDropdown,
    isSearchable,
    isMultiSelect,
    items,
    searchQuery
  });

  const handleToggleOption = (value: string) => {
    toggleOption(value);
    // close popover after selecting for single select
    if (!isMultiSelect) {
      setPopoverOpen(false);
      setSearchQuery("");
    }
  };

  const handleRemoveOption = (value: string) => {
    const updatedOptions = selectedValues?.filter((v) => v !== value) || [];
    onChange(updatedOptions);
  };

  const handleClearAll = () => {
    clearAll();
    setSearchQuery("");
  };

  const handleAccept = () => {
    setPopoverOpen(false);
    setSearchQuery("");
    onAccept?.();
  };

  const selectSize = variant === SelectVariant.CHIP ? SelectSize.EXTRA_SMALL : size;
  const shouldOpenOnInputFocus = openOnFocus && !isChipVariant;

  const totalSelectedCount = selectedValues?.length;

  const selectElement = (
    <Popover
      isOpen={isPopoverOpen}
      onOpenChange={setPopoverOpen}
      position={position}
      disabled={disabled}
      id={selectId}
      className="flex max-h-[calc(var(--radix-popover-content-available-height)-16px)] flex-col"
      styles={styles.popover}
      aria-required={required}
      onEscapeKeyDown={() => setPopoverOpen(false)}
      onInteractOutside={(event) => {
        const eventTarget = event.target;
        if (eventTarget instanceof Node && selectContainerRef.current?.contains(eventTarget)) {
          return;
        }

        setPopoverOpen(false);
      }}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        inputRef.current?.focus();
      }}
      trigger={
        customTrigger ? (
          // Attach selectContainerRef so onInteractOutside recognizes a click on
          // the custom trigger as "inside". Without it, clicking the trigger while
          // open force-closes via onInteractOutside and Radix's trigger toggle
          // immediately reopens it — so the trigger can't close the menu. The
          // default SelectTrigger already wires this ref; mirror it here.
          <div ref={selectContainerRef} className="w-fit">
            {customTrigger({ isOpen: isPopoverOpen })}
          </div>
        ) : (
          <SelectTrigger
            chipContainerRef={chipContainerRef}
            disabled={disabled}
            error={error}
            hasSearchTrigger={hasSearchTrigger}
            hideClear={hideClear}
            inputRef={inputRef}
            isChipVariant={isChipVariant}
            isInputFocused={isInputFocused}
            isMultiSelect={isMultiSelect}
            isPopoverOpen={isPopoverOpen}
            name={name}
            onClearAll={handleClearAll}
            onInputBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setIsInputFocused(false);
              }
            }}
            onInputChange={(e) => setSearchQuery(e.target.value)}
            onInputFocus={() => {
              setIsInputFocused(true);
              if (!disabled && !isPopoverOpen && shouldOpenOnInputFocus) {
                setPopoverOpen(true);
              }
            }}
            onRemoveOption={handleRemoveOption}
            placeholder={placeholder}
            required={required}
            searchQuery={searchQuery}
            selectContainerRef={selectContainerRef}
            selectedOptions={selectedOptions}
            showSublabelInTrigger={showSublabelInTrigger}
            size={selectSize}
            variant={variant}
            visibleChipCount={visibleChipCount}
            id={selectId}
          />
        )
      }
    >
      {hasSearchInDropdown && (
        <SelectDropdownSearch disabled={disabled} inputRef={inputRef} searchQuery={searchQuery} onSearchQueryChange={setSearchQuery} />
      )}
      {/* options list */}
      <OptionsList
        items={filteredItems}
        loading={loading}
        searchQuery={searchQuery}
        selectedValues={selectedValues}
        handleToggleOption={handleToggleOption}
        isMultiSelect={isMultiSelect}
        emptyStateNote={emptyStateNote}
      />

      {/* multi-select footer */}
      {isMultiSelect && <DropdownFooter totalCount={totalSelectedCount} handleClearAll={handleClearAll} handleAccept={handleAccept} />}
    </Popover>
  );

  const widthClass = getFormFieldWidthClass(width);

  if (customTrigger) {
    return <>{selectElement}</>;
  }

  if (!label) {
    return (
      <div className={widthClass}>
        {selectElement}
        {error && <span className="text-sm text-red-500">{error instanceof Error ? error.message : error}</span>}
      </div>
    );
  }

  return (
    <div className={filterClasses(widthClass, "flex", labelPosition === LabelPosition.TOP ? "flex-col gap-1" : "flex-row items-center gap-4")}>
      <Label htmlFor={hasSearchTrigger ? `${selectId}-input` : selectId} required={required}>
        {label}
      </Label>
      {selectElement}
      {error && <span className="text-sm text-red-500">{error instanceof Error ? error.message : error}</span>}
    </div>
  );
};

// compound components
const SelectOption = ({ children, value }: SelectOptionProps) => null;
const SelectGroup = ({ children, label }: SelectGroupProps) => null;
const SelectDivider = ({ label }: SelectDividerProps) => null;

// attach compound components to Select
Select.Option = SelectOption;
Select.Group = SelectGroup;
Select.Divider = SelectDivider;

export { Select };
